import { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ExternalLink, CheckCircle, ArrowLeft, RefreshCw, X, Banknote, Search } from 'lucide-react';
import { statementsApi, previewUrl, documentPdfUrl, statementPdfUrl } from '../services/api';
import { Btn, Card, Alert, Spinner, Modal, Input, Select, Badge, DropButton, ConfirmDialog, Pill, SearchField } from '../components/UI';
import { useToast } from '../components/Toast';
import TxMatchModal from '../components/TxMatchModal';
import { AuthedLink } from '../components/AuthedFile';
import { Pagination } from '../components/Pagination';
import { huFmt, fmtIban, fmtDateHu } from '../utils';

const _pad = n => String(n).padStart(2, '0');
const _ymd = (dt) => `${dt.getFullYear()}-${_pad(dt.getMonth()+1)}-${_pad(dt.getDate())}`;
const todayYmd = () => _ymd(new Date());
const daysAgoYmd = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return _ymd(d); };

// ⚠ 2026-09-16: a közös formázóra állt át (ÉÉÉÉ.HH.NN, `CLAUDE.md` §4).
// Eddig ISO-t hagyott (`2026-08-05`), egyedüliként a Bizonylatok/Dashboard/
// Partnerek oldalak helyi implementációi közül.
const fmtDate = fmtDateHu;
const fmtAmt  = (v, cur='HUF') => v == null ? '–' :
  `${huFmt(v, cur === 'HUF' ? 0 : 2)} ${cur}`;

const FEE_TYPE_LABELS = {
  monthly:     'Havi díj',
  transaction: 'Tranzakciós díj',
  giro:        'GIRO jutalék',
  fx:          'FX díj',
  custody:     'Letétkezelési',
  card:        'Kártya díj',
  other:       'Egyéb díj',
};
const FEE_TYPE_OPTIONS = Object.entries(FEE_TYPE_LABELS).map(([v,l])=>({value:v,label:l}));

// Konzisztens bank-szín minden vizuális elem között.
// A színek úgy vannak megválasztva, hogy NE ütközzenek a deviza-színekkel
// (HUF=#dc2626 piros, EUR=#2563eb kék, USD=#16a34a zöld).
const KNOWN_BANK_COLORS = {
  'MBH':             '#14b8a6', // türkiz-zöld (MBH logó tónusa)
  'Revolut':         '#0c2c5e', // sötétkék
  'Revolut Bank':    '#0c2c5e',
  'Bank Burgenland': '#f87171', // pasztell piros (eltér a deviza-pirostól)
  'Erste Bank':      '#1e40af',
  'OTP':             '#15803d',
  'K&H':             '#7c3aed',
  'Raiffeisen':      '#facc15',
};
const FALLBACK_PALETTE = ['#a855f7','#f97316','#06b6d4','#ec4899','#84cc16','#eab308','#fb7185'];

export function bankColor(name) {
  if (!name) return '#6b7280';
  // exact match
  if (KNOWN_BANK_COLORS[name]) return KNOWN_BANK_COLORS[name];
  // prefix match (pl. "Revolut Business Bank" → Revolut)
  for (const k of Object.keys(KNOWN_BANK_COLORS)) {
    if (name.toLowerCase().includes(k.toLowerCase())) return KNOWN_BANK_COLORS[k];
  }
  // deterministic hash → fallback palette
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return FALLBACK_PALETTE[h % FALLBACK_PALETTE.length];
}

export function BankDot({ name, size = 8 }) {
  return (
    <span style={{
      display:'inline-block',width:size,height:size,borderRadius:'50%',
      background:bankColor(name),flexShrink:0,
      boxShadow:'0 0 0 1px rgba(0,0,0,0.15)',
    }}/>
  );
}

// Deviza-szín – konzisztens az összes pénzügyi nézetben.
const CURRENCY_COLORS = {
  HUF: '#dc2626',   // piros
  EUR: '#2563eb',   // kék
  USD: '#16a34a',   // zöld
  GBP: '#7c3aed',   // lila
  CHF: '#0ea5e9',   // világoskék
};
export function currencyColor(cur) {
  return CURRENCY_COLORS[(cur || '').toUpperCase()] || '#6b7280';
}
export function CurrencyBadge({ cur, size = 'sm' }) {
  const c = currencyColor(cur);
  const padding = size === 'lg' ? '0.25rem 0.55rem' : '0.12rem 0.4rem';
  const fontSize = size === 'lg' ? '0.78rem' : '0.66rem';
  return (
    <span style={{
      display:'inline-block',
      background: c + '1f',                // ~12% opacity background
      border: `1px solid ${c}55`,
      color: c,
      padding, fontSize, fontWeight: 700, letterSpacing: '0.04em',
      borderRadius: 'var(--radius)',
      fontFamily: 'var(--mono)',
    }}>{cur || '?'}</span>
  );
}

function FeeBadge({ feeType }) {
  return (
    <span style={{display:'inline-flex',alignItems:'center',gap:4,
      background:'rgba(245,158,11,0.12)',border:'1px solid rgba(245,158,11,0.35)',
      color:'#f59e0b',padding:'0.18rem 0.5rem',borderRadius:'var(--radius)',
      fontSize:'0.7rem',fontWeight:600,letterSpacing:'0.02em'}}>
      <Banknote size={11}/> BANKKÖLTSÉG
      {feeType && (
        <span style={{fontWeight:400,opacity:0.85}}>· {FEE_TYPE_LABELS[feeType] || feeType}</span>
      )}
    </span>
  );
}

// FX (deviza-átváltás) jelző – konverzió (saját EUR↔HUF) vagy deviza vevői fizetés.
// Tooltip: árfolyam + másik oldal + FX-díj.
function FxBadge({ tx }) {
  const isConv = tx.fx_kind === 'conversion';
  const c = '#0ea5e9';
  // teljes FX-költség HUF-ban: látható díj (ha HUF) + becsült árfolyamrés
  const feeHuf = (tx.fx_fee != null && tx.currency === 'HUF') ? tx.fx_fee : 0;
  const totalCost = feeHuf + (tx.fx_spread_huf != null ? tx.fx_spread_huf : 0);
  const parts = [];
  if (tx.fx_rate != null) parts.push(`Árfolyam: 1 ${tx.currency} = ${tx.fx_rate} ${tx.fx_counter_currency || ''}`);
  if (tx.fx_mid_rate != null) parts.push(`MNB-középárfolyam: ${tx.fx_mid_rate}`);
  if (tx.fx_counter_amount != null) parts.push(`Másik oldal: ${fmtAmt(tx.fx_counter_amount, tx.fx_counter_currency)}`);
  if (tx.fx_fee != null) parts.push(`Látható díj: ${fmtAmt(tx.fx_fee, tx.currency)}`);
  if (tx.fx_spread_huf != null) parts.push(`Becsült árfolyamrés: ${fmtAmt(tx.fx_spread_huf, 'HUF')}`);
  return (
    <span title={parts.join(' · ')} style={{display:'inline-flex',alignItems:'center',gap:4,
      background:c+'1f',border:`1px solid ${c}66`,color:c,padding:'0.18rem 0.5rem',
      borderRadius:'var(--radius)',fontSize:'0.7rem',fontWeight:600,letterSpacing:'0.02em'}}>
      FX · {isConv ? 'átváltás' : 'deviza'}
      {tx.fx_counter_amount != null && (
        <span style={{fontWeight:400,opacity:0.85}}>· {fmtAmt(tx.fx_counter_amount, tx.fx_counter_currency)}</span>
      )}
      {totalCost > 0 && (
        <span style={{fontWeight:400,opacity:0.85}} title="Látható díj + becsült árfolyamrés (MNB-középhez képest)">
          · költség ~{fmtAmt(totalCost, 'HUF')}</span>
      )}
    </span>
  );
}

function TabBar({ tab, setTab }) {
  const tabBtnS = (active) => ({
    // K-11: token, nem beégetett fehér (Raymondban a sárgán 1,27:1 volt)
    background: active ? 'var(--tab-active-bg)' : 'transparent',
    color: active ? 'var(--tab-active-fg)' : 'var(--text-muted)',
    border: '1px solid ' + (active ? 'var(--tab-active-bg)' : 'var(--border)'),
    borderRadius: 'var(--radius)', padding: '0.4rem 0.9rem',
    fontSize: '0.8rem', cursor: 'pointer', fontWeight: active ? 600 : 400,
  });
  return (
    <div style={{display:'flex',gap:8,marginBottom:'0.9rem'}}>
      <button style={tabBtnS(tab==='list')} onClick={()=>setTab('list')}>Kivonatok</button>
      <button style={tabBtnS(tab==='txs')} onClick={()=>setTab('txs')}>Tranzakciók</button>
    </div>
  );
}

export default function Statements() {
  const [tab, setTab] = useState('list');
  const bar = <TabBar tab={tab} setTab={setTab}/>;
  return (
    <div>
      {tab === 'list' && <StatementsList tabBar={bar}/>}
      {tab === 'txs'  && <AllTransactions tabBar={bar}/>}
    </div>
  );
}


function StatementsList({ tabBar }) {
  const qc = useQueryClient();
  const [detailId, setDetailId]   = useState(null);
  const [uploadResults, setUploadResults] = useState([]);  // {name, status, text}
  const [uploadBusy, setUploadBusy] = useState(false);
  // Szűrők
  const [dateFilter, setDateFilter] = useState('all');     // all | year | last-month | custom
  const [range, setRange]   = useState({ from: daysAgoYmd(7), to: todayYmd() });
  const [bankSel, setBankSel] = useState(new Set());
  const [curSel, setCurSel] = useState('all');             // all | HUF | EUR | USD | other
  const [noPdf, setNoPdf]   = useState(false);
  const [q, setQ]           = useState('');
  const toast = useToast();

  // ⚠ 2026-08-24: a dátum/bank/deviza/PDF/kereső szűrők KORÁBBAN kliens-
  // oldaliak voltak (csak az aktuális lekért oldalt szűrték) — Gábor kérésére
  // most szerver-oldaliak, ugyanúgy, mint a Bizonylatok oldalon. A `filterParams`
  // SZÁNDÉKOSAN nem tartalmazza a page/viewMode-ot, hogy a lenti useEffect csak
  // valódi szűrő-változásra állítsa 1-re a page-et.
  const filterParams = useMemo(() => {
    const p = {};
    if (bankSel.size) p.bank_name = [...bankSel].join(',');
    if (curSel === 'other') p.currency_other = true;
    else if (curSel !== 'all') p.currency = curSel;
    if (noPdf) p.has_pdf = false;
    if (q.trim()) p.q = q.trim();
    if (dateFilter === 'year') {
      const y = new Date().getFullYear();
      p.date_from = `${y}-01-01`; p.date_to = `${y}-12-31`;
    } else if (dateFilter === 'last-month') {
      const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() - 1);
      const y = d.getFullYear(), m = d.getMonth();
      p.date_from = _ymd(new Date(y, m, 1));
      p.date_to   = _ymd(new Date(y, m + 1, 0));
    } else if (dateFilter === 'custom') {
      if (range.from) p.date_from = range.from;
      if (range.to)   p.date_to   = range.to;
    }
    return p;
  }, [bankSel, curSel, noPdf, q, dateFilter, range]);

  // Lapozás (wiki/proposals/pagination.md).
  const [page, setPage] = useState(1);
  const [viewMode, setViewMode] = useState('100');
  useEffect(() => { setPage(1); }, [filterParams]);
  const handleViewModeChange = (v) => { setViewMode(v); setPage(1); };
  const listParams = useMemo(() => ({ ...filterParams, page, page_size: viewMode }),
    [filterParams, page, viewMode]);
  const { data: statementsPage, isLoading, isError: listError, refetch: refetchList } = useQuery({
    queryKey: ['statements', listParams],
    queryFn: () => statementsApi.list(listParams),
  });
  const statements = statementsPage?.items ?? [];

  // A bank-szűrő pill-lista STABIL forrásból jön (az összes bankszámla neve),
  // nem az aktuális (már szűrt/lapozott) `statements`-ből — különben egy
  // aktív szűrő eltüntetné a többi bank választási lehetőségét.
  const { data: allAccounts = [] } = useQuery({
    queryKey: ['statement-accounts'],
    queryFn: statementsApi.accounts,
  });
  const banks = [...new Set(allAccounts.map(a => a.bank_name).filter(Boolean))].sort();

  const processFiles = async (fileList) => {
    const files = Array.from(fileList || []).filter(f =>
      /\.pdf$/i.test(f.name) || f.type === 'application/pdf');
    if (!files.length || uploadBusy) return;
    setUploadBusy(true);
    setUploadResults(files.map(f => ({ name: f.name, status: 'working', text: '' })));
    for (let i = 0; i < files.length; i++) {
      let status = 'ok', text = '';
      try {
        const r = await statementsApi.upload(files[i]);
        if (r.saved) {
          text = `${r.bank_name || 'Bank'} (${r.iban || '?'}) ${r.period_from}…${r.period_to}: ${r.transactions} tranzakció, auto-match futtatva`;
        } else { status = 'info'; text = r.reason || 'Már létezik'; }
      } catch (e) {
        status = 'err';
        const d = e?.response?.data?.detail;
        text = typeof d === 'string' ? d : (e.message || 'hiba');
      }
      setUploadResults(prev => prev.map((it, idx) => idx === i ? { ...it, status, text } : it));
    }
    setUploadBusy(false);
    qc.invalidateQueries({ queryKey: ['statements'] });
  };

  const approveMut = useMutation({
    mutationFn: (id) => statementsApi.approve(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['statements'] });
      toast.success('Kivonat jóváhagyva');
    },
  });

  if (detailId) {
    return (
      <div>
        {tabBar}
        <StatementDetail id={detailId} onBack={()=>setDetailId(null)}/>
      </div>
    );
  }

  const toggleBank = (b) => setBankSel(prev => {
    const next = new Set(prev);
    next.has(b) ? next.delete(b) : next.add(b);
    return next;
  });

  const sepS = { width:1, height:18, background:'var(--border)', margin:'0 4px' };

  const tdS = { padding:'0.55rem 0.75rem', fontSize:'0.8rem', verticalAlign:'middle',
                borderBottom:'1px solid var(--border-dim)' };
  const thS = { padding:'0.6rem 0.75rem', color:'var(--text-muted)', fontWeight:600,
                fontSize:'0.7rem', textTransform:'uppercase', letterSpacing:'0.06em',
                borderBottom:'1px solid var(--border)', whiteSpace:'nowrap' };

  return (
    <div>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:'1rem',flexWrap:'wrap',gap:8}}>
        <div>
          <h1 style={{fontSize:'1.25rem',fontWeight:700}}>Bankszámla kivonatok</h1>
          <p style={{fontSize:'0.8rem',color:'var(--text-muted)',marginTop:2}}>
            {statementsPage ? `${statementsPage.total_items} kivonat` : '…'}
          </p>
        </div>
        <div style={{display:'flex',gap:8,flexWrap:'wrap',alignItems:'center'}}>
          <DropButton onFiles={processFiles} busy={uploadBusy} accept="application/pdf"/>
        </div>
      </div>

      {tabBar}

      {uploadResults.length > 0 && (
        <div style={{background:'var(--bg-card)',border:'1px solid var(--border)',
          borderRadius:'var(--radius-lg)',padding:'0.7rem 1rem',marginBottom:'1rem',
          display:'flex',flexDirection:'column',gap:4}}>
          {uploadResults.map((it, i) => (
            <div key={i} style={{display:'flex',alignItems:'center',gap:8,fontSize:'0.78rem'}}>
              {it.status === 'working' && <Spinner size={12}/>}
              <Badge variant={it.status==='ok' ? 'success' : it.status==='info' ? 'warning'
                : it.status==='err' ? 'danger' : 'default'}>
                {it.status==='ok' ? 'Mentve' : it.status==='info' ? 'Kihagyva'
                  : it.status==='err' ? 'Hiba' : 'Feldolgozás…'}
              </Badge>
              <span style={{fontFamily:'var(--mono)'}}>{it.name}</span>
              <span style={{color:'var(--text-muted)'}}>{it.text}</span>
            </div>
          ))}
          {!uploadBusy && (
            <button onClick={()=>setUploadResults([])}
              style={{alignSelf:'flex-start',background:'none',border:'none',
                color:'var(--text-dim)',cursor:'pointer',fontSize:'0.72rem',padding:0,marginTop:2}}>
              Lista elrejtése
            </button>
          )}
        </div>
      )}

      {/* ── Szűrők ──────────────────────────────────────────────────────── */}
      <div style={{display:'flex',gap:6,marginBottom:'0.75rem',flexWrap:'wrap',alignItems:'center'}}>
        <Pill active={dateFilter==='all'} onClick={()=>setDateFilter('all')}>Mind</Pill>
        <Pill active={dateFilter==='year'} onClick={()=>setDateFilter(d=>d==='year'?'all':'year')}>Aktuális év</Pill>
        <Pill active={dateFilter==='last-month'} onClick={()=>setDateFilter(d=>d==='last-month'?'all':'last-month')}>Előző hónap</Pill>
        <Pill active={dateFilter==='custom'} onClick={()=>setDateFilter(d=>d==='custom'?'all':'custom')}>Egyéni</Pill>
        {dateFilter==='custom' && (
          <>
            <input type="date" value={range.from} onChange={e=>setRange(r=>({...r,from:e.target.value}))}
              style={{background:'var(--bg-input)',border:'1px solid var(--border)',borderRadius:'var(--radius)',
                padding:'0.22rem 0.45rem',color:'var(--text)',fontSize:'0.74rem'}}/>
            <input type="date" value={range.to} onChange={e=>setRange(r=>({...r,to:e.target.value}))}
              style={{background:'var(--bg-input)',border:'1px solid var(--border)',borderRadius:'var(--radius)',
                padding:'0.22rem 0.45rem',color:'var(--text)',fontSize:'0.74rem'}}/>
          </>
        )}

        <span style={sepS}/>
        {banks.map(b => (
          <Pill key={b} active={bankSel.has(b)} onClick={()=>toggleBank(b)}
            style={{display:'inline-flex',alignItems:'center',gap:6}}>
            <BankDot name={b} size={8}/>{b}
          </Pill>
        ))}

        <span style={sepS}/>
        {['all','HUF','EUR','USD','other'].map(c => (
          <Pill key={c} active={curSel===c} onClick={()=>setCurSel(c)}>
            {c==='all' ? 'Mind' : c==='other' ? 'Egyéb' : c}
          </Pill>
        ))}

        <span style={sepS}/>
        <Pill active={noPdf} onClick={()=>setNoPdf(v=>!v)}>PDF nélkül</Pill>

        <SearchField placeholder="Keresés (bank, IBAN, időszak…)" value={q}
          onChange={setQ} onClear={()=>setQ('')} style={{flex:1,minWidth:180}}/>
      </div>

      {statementsPage && (
        <div style={{display:'flex',justifyContent:'flex-end',marginBottom:8}}>
          <Pagination
            mode={statementsPage.mode} page={statementsPage.page}
            totalPages={statementsPage.total_pages} pageLabel={statementsPage.page_label}
            onPageChange={setPage} viewMode={viewMode} onViewModeChange={handleViewModeChange}
            showViewSelector/>
        </div>
      )}
      <Card noPad>
        <div style={{overflowX:'auto'}}>
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:'0.82rem'}}>
            <thead>
              <tr>
                <th style={thS}>Bank / IBAN</th>
                <th style={thS}>Periódus</th>
                <th style={{...thS,textAlign:'right'}}>Nyitó</th>
                <th style={{...thS,textAlign:'right'}}>Záró</th>
                <th style={thS}>Forrás</th>
                <th style={thS}>Tranzakciók</th>
                <th style={thS}>PDF</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr><td colSpan={7} style={{...tdS,textAlign:'center',padding:'2rem'}}><Spinner/></td></tr>
              ) : listError ? (
                <tr><td colSpan={7} style={{...tdS,padding:'1rem'}}>
                  <Alert type="danger" style={{marginBottom:0,display:'flex',alignItems:'center',
                    justifyContent:'space-between',gap:12,flexWrap:'wrap'}}>
                    <span>Betöltés sikertelen.</span>
                    <Btn size="sm" variant="secondary" onClick={()=>refetchList()}>Újra</Btn>
                  </Alert>
                </td></tr>
              ) : statements.length === 0 ? (
                <tr><td colSpan={7} style={{...tdS,textAlign:'center',color:'var(--text-muted)',padding:'2rem'}}>
                  {statements.length === 0
                    ? 'Nincs még kivonat. Töltsd fel az első PDF-et, vagy küldj egy banki kivonatot az invoice@-ra.'
                    : 'Nincs a szűrőknek megfelelő kivonat.'}
                </td></tr>
              ) : statements.map(s => {
                const matchFull = s.tx_total > 0 && s.tx_matched === s.tx_total;
                const isOwnerPending = s.review_status === 'PENDING_OWNER';
                const isBalanceReview = s.review_status === 'NEEDS_REVIEW';
                const isPending = isOwnerPending || isBalanceReview;
                const warnTitle = isBalanceReview
                  ? 'Egyenleg-eltérés – az AI által kiolvasott tételek nem adják ki a záró egyenleget. Kézi ellenőrzés szükséges.'
                  : `Tulajdonos: ${s.holder_name || '?'} – nem egyezik a céggel, manuális jóváhagyás kell`;
                return (
                  <tr key={s.id} style={{
                      cursor:'pointer',transition:'background 0.1s',
                      background: isPending ? 'rgba(245,158,11,0.06)' : undefined,
                    }}
                    onMouseEnter={e=>e.currentTarget.style.background = isPending ? 'rgba(245,158,11,0.12)' : 'var(--bg-hover)'}
                    onMouseLeave={e=>e.currentTarget.style.background = isPending ? 'rgba(245,158,11,0.06)' : 'transparent'}
                    onClick={()=>setDetailId(s.id)}>
                    <td style={tdS}>
                      <div style={{display:'flex',alignItems:'center',gap:8}}>
                        {isPending && (
                          <span title={warnTitle}
                            style={{fontSize:'0.7rem',color:'#f59e0b',fontWeight:700}}>⚠</span>
                        )}
                        <BankDot name={s.bank_name} size={10}/>
                        <div>
                          <div style={{fontWeight:500}}>{s.bank_name || '–'}</div>
                          <div style={{fontSize:'0.68rem',color:'var(--text-muted)',fontFamily:'var(--mono)'}}>
                            {fmtIban(s.iban)}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td style={tdS}>
                      <span style={{fontFamily:'var(--mono)'}}>
                        {fmtDate(s.period_from)} … {fmtDate(s.period_to)}
                      </span>
                    </td>
                    <td style={{...tdS,textAlign:'right',fontFamily:'var(--mono)'}}>
                      {fmtAmt(s.opening_balance, s.currency)}
                    </td>
                    <td style={{...tdS,textAlign:'right',fontFamily:'var(--mono)',fontWeight:600}}>
                      {fmtAmt(s.closing_balance, s.currency)}
                    </td>
                    <td style={tdS}>
                      <span style={{fontSize:'0.7rem',padding:'0.18rem 0.5rem',borderRadius:'var(--radius)',
                        background:'rgba(255,255,255,0.06)',color:'var(--text-muted)'}}>
                        {s.source === 'REVOLUT_API' ? 'Revolut' :
                         s.source === 'RAIFFEISEN_API' ? 'Raiffeisen' :
                         s.source === 'EMAIL_ATTACHMENT' ? 'E-mail' : 'Manuális'}
                      </span>
                    </td>
                    <td style={tdS}>
                      <span style={{display:'inline-flex',alignItems:'center',gap:6}}>
                        {matchFull && <CheckCircle size={13} color="var(--accent)"/>}
                        <span style={{fontFamily:'var(--mono)',fontSize:'0.78rem'}}>
                          {s.tx_matched} / {s.tx_total}
                        </span>
                      </span>
                    </td>
                    <td style={tdS} onClick={e=>e.stopPropagation()}>
                      <span style={{display:'inline-flex',alignItems:'center',gap:8}}>
                        {isPending && (
                          <Btn size="sm" variant="secondary"
                            loading={approveMut.isPending && approveMut.variables===s.id}
                            onClick={()=>approveMut.mutate(s.id)}>
                            Jóváhagy
                          </Btn>
                        )}
                        {s.pdf_onedrive_url && (
                          <AuthedLink href={statementPdfUrl(s.id)}
                            style={{color:'var(--primary)',display:'inline-flex',alignItems:'center',gap:3,fontSize:'0.75rem'}}>
                            <ExternalLink size={11}/> Megnyit
                          </AuthedLink>
                        )}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
      {statementsPage && (
        <div style={{display:'flex',justifyContent:'flex-end',marginTop:8}}>
          <Pagination
            mode={statementsPage.mode} page={statementsPage.page}
            totalPages={statementsPage.total_pages} pageLabel={statementsPage.page_label}
            onPageChange={setPage} viewMode={viewMode} onViewModeChange={handleViewModeChange}/>
        </div>
      )}

    </div>
  );
}


function StatementSummary({ stmt, txs }) {
  // Devizánkénti aggregálás. Bankköltség KIVÉTEL a debit szummából.
  const acc = {};
  for (const tx of txs) {
    const cur = tx.currency || stmt?.currency || 'HUF';
    if (!acc[cur]) acc[cur] = { credit:{c:0,s:0}, debit:{c:0,s:0}, fee:{c:0,s:0} };
    const amt = Number(tx.amount) || 0;
    if (tx.is_bank_fee) {
      acc[cur].fee.c++; acc[cur].fee.s += amt;
    } else if (String(tx.tx_type).toLowerCase() === 'credit') {
      acc[cur].credit.c++; acc[cur].credit.s += amt;
    } else {
      acc[cur].debit.c++; acc[cur].debit.s += amt;
    }
  }
  const labelS = { fontSize:'0.65rem',color:'var(--text-muted)',textTransform:'uppercase',
                   letterSpacing:'0.06em',fontWeight:600 };
  const valS   = { fontFamily:'var(--mono)',fontSize:'0.9rem',fontWeight:600 };
  const cellS  = { display:'flex',flexDirection:'column',gap:2 };
  return (
    <div style={{marginTop:'0.75rem'}}>
      {Object.entries(acc).map(([cur, v]) => {
        const net = v.credit.s - v.debit.s - v.fee.s;
        return (
          <Card key={cur} style={{marginBottom:'0.5rem'}}>
            <div style={{display:'grid',
              gridTemplateColumns:'repeat(auto-fit, minmax(140px, 1fr))',gap:'1rem',alignItems:'center'}}>
              <div style={cellS}>
                <span style={labelS}>Deviza</span>
                <span style={{...valS,color:'var(--text)'}}>{cur}</span>
              </div>
              <div style={cellS}>
                <span style={labelS}>Nyitó egyenleg</span>
                <span style={valS}>{stmt?.opening_balance != null ? fmtAmt(stmt.opening_balance, cur) : '–'}</span>
              </div>
              <div style={cellS}>
                <span style={labelS}>Credit ({v.credit.c})</span>
                <span style={{...valS,color:'var(--accent)'}}>+{fmtAmt(v.credit.s, cur)}</span>
              </div>
              <div style={cellS}>
                <span style={labelS}>Debit ({v.debit.c})</span>
                <span style={valS}>−{fmtAmt(v.debit.s, cur)}</span>
              </div>
              <div style={cellS}>
                <span style={labelS}>Bankköltség ({v.fee.c})</span>
                <span style={{...valS,color:'#f59e0b'}}>−{fmtAmt(v.fee.s, cur)}</span>
              </div>
              <div style={cellS}>
                <span style={labelS}>Záró egyenleg</span>
                <span style={valS}>{stmt?.closing_balance != null ? fmtAmt(stmt.closing_balance, cur) : '–'}</span>
              </div>
              <div style={cellS}>
                <span style={labelS}>Nettó mozgás</span>
                <span style={{...valS,color: net >= 0 ? 'var(--accent)' : '#ef4444'}}>
                  {net >= 0 ? '+' : '−'}{fmtAmt(Math.abs(net), cur)}
                </span>
              </div>
            </div>
          </Card>
        );
      })}
    </div>
  );
}


function StatementDetail({ id, onBack }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [matchTx, setMatchTx] = useState(null);   // bizonylat-kereső modal tranzakciója
  const [confirm, setConfirm] = useState(null);   // {title, message, danger, confirmLabel, action}
  const [feeModal, setFeeModal] = useState(null); // {txId} – díj-típus választó
  const [feeType, setFeeType] = useState('other');
  const { data: stmt, isError: stmtError, refetch: refetchStmt } = useQuery({
    queryKey: ['statement', id],
    queryFn: () => statementsApi.get(id),
  });
  const { data: txs = [], isLoading, isError: txsError, refetch: refetchTxs } = useQuery({
    queryKey: ['statement-txs', id],
    queryFn: () => statementsApi.transactions(id),
  });

  const unmatchMut = useMutation({
    mutationFn: ({txId, docId}) => statementsApi.unmatch(txId, docId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['statement-txs', id] }),
  });
  const rematchMut = useMutation({
    mutationFn: () => statementsApi.rematch(id),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['statement-txs', id] });
      qc.invalidateQueries({ queryKey: ['statements'] });
      toast.success(`Újrafuttatás kész: ${data.matched} új társítás.`);
    },
    onError: (err) => toast.error(`Hiba a rematch-nél: ${err?.response?.data?.detail || err.message}`),
  });
  const feeMut = useMutation({
    mutationFn: ({txId, isBankFee, feeType}) => statementsApi.toggleFee(txId, isBankFee, feeType),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['statement-txs', id] }),
  });

  const tdS = { padding:'0.5rem 0.65rem', fontSize:'0.78rem', verticalAlign:'top',
                borderBottom:'1px solid var(--border-dim)' };
  const thS = { padding:'0.55rem 0.65rem', color:'var(--text-muted)', fontWeight:600,
                fontSize:'0.68rem', textTransform:'uppercase', letterSpacing:'0.06em',
                borderBottom:'1px solid var(--border)', whiteSpace:'nowrap' };

  return (
    <div>
      <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:'1rem'}}>
        <Btn variant="secondary" icon={ArrowLeft} onClick={onBack}>Vissza</Btn>
        <div style={{flex:1}}>
          <h1 style={{fontSize:'1.1rem',fontWeight:700,display:'flex',alignItems:'center',gap:10}}>
            <BankDot name={stmt?.bank_name} size={12}/>
            <span>{stmt?.bank_name}</span>
            <span style={{fontFamily:'var(--mono)',color:'var(--text-muted)',fontWeight:400,fontSize:'0.85rem'}}>{fmtIban(stmt?.iban)}</span>
          </h1>
          <p style={{fontSize:'0.78rem',color:'var(--text-muted)'}}>
            {fmtDate(stmt?.period_from)} … {fmtDate(stmt?.period_to)} ·
            nyitó {fmtAmt(stmt?.opening_balance, stmt?.currency)} → záró {fmtAmt(stmt?.closing_balance, stmt?.currency)}
          </p>
        </div>
        <Btn variant="ghost" size="sm" icon={RefreshCw} loading={rematchMut.isPending}
          onClick={()=>rematchMut.mutate()}>
          Rematch
        </Btn>
        {stmt?.pdf_onedrive_url && (
          <AuthedLink href={statementPdfUrl(stmt.id)}
            style={{color:'var(--primary)',display:'inline-flex',alignItems:'center',gap:4,fontSize:'0.82rem'}}>
            <ExternalLink size={13}/> PDF megnyit
          </AuthedLink>
        )}
      </div>

      <Card noPad>
        <div style={{overflowX:'auto'}}>
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:'0.78rem'}}>
            <thead>
              <tr>
                <th style={thS}>Dátum</th>
                <th style={thS}>Partner</th>
                <th style={thS}>IBAN</th>
                <th style={thS}>Közlemény</th>
                <th style={{...thS,textAlign:'right'}}>Összeg</th>
                <th style={thS}>Match</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr><td colSpan={6} style={{...tdS,textAlign:'center',padding:'1.5rem'}}><Spinner/></td></tr>
              ) : (txsError || stmtError) ? (
                <tr><td colSpan={6} style={{...tdS,padding:'1rem'}}>
                  <Alert type="danger" style={{marginBottom:0,display:'flex',alignItems:'center',
                    justifyContent:'space-between',gap:12,flexWrap:'wrap'}}>
                    <span>Betöltés sikertelen.</span>
                    <Btn size="sm" variant="secondary"
                      onClick={()=>{ if (stmtError) refetchStmt(); if (txsError) refetchTxs(); }}>
                      Újra
                    </Btn>
                  </Alert>
                </td></tr>
              ) : txs.length === 0 ? (
                <tr><td colSpan={6} style={{...tdS,textAlign:'center',color:'var(--text-muted)',padding:'1.5rem'}}>
                  Nincs tranzakció.
                </td></tr>
              ) : txs.map(tx => {
                const isDebit = String(tx.tx_type).toLowerCase() === 'debit';
                return (
                <tr key={tx.id} style={{background: tx.is_bank_fee ? 'rgba(245,158,11,0.04)' : undefined}}>
                  <td style={{...tdS,fontFamily:'var(--mono)',whiteSpace:'nowrap'}}>{fmtDate(tx.tx_date)}</td>
                  <td style={tdS}>
                    <div style={{fontWeight:500}}>{tx.counterparty_name || (tx.is_bank_fee ? '— díj —' : '–')}</div>
                  </td>
                  <td style={{...tdS,fontFamily:'var(--mono)',fontSize:'0.7rem',color:'var(--text-muted)'}}>
                    {tx.counterparty_iban ? fmtIban(tx.counterparty_iban) : '–'}
                  </td>
                  <td style={{...tdS,maxWidth:340}}>
                    <div style={{fontSize:'0.72rem',color:'var(--text-muted)',
                      overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                      {tx.reference || '–'}
                    </div>
                  </td>
                  <td style={{...tdS,textAlign:'right',fontFamily:'var(--mono)',fontWeight:600,
                    color: !isDebit ? 'var(--accent)' : 'inherit', whiteSpace:'nowrap'}}>
                    {isDebit ? '−' : '+'}{fmtAmt(tx.amount, tx.currency)}
                  </td>
                  <td style={tdS}>
                    {tx.is_fx && <FxBadge tx={tx}/>}
                    {tx.fx_kind === 'conversion' ? null : tx.is_bank_fee ? (
                      <div style={{display:'flex',alignItems:'center',gap:6}}>
                        <FeeBadge feeType={tx.fee_type}/>
                        <button
                          onClick={()=>setConfirm({
                            title: 'Mégse banki díj?',
                            message: 'Visszaállítjuk normál tranzakcióvá.',
                            confirmLabel: 'Visszaállítás',
                            action: () => feeMut.mutate({txId:tx.id, isBankFee:false, feeType:null}),
                          })}
                          title="Mégse díj"
                          style={{background:'none',border:'1px solid var(--border)',
                            color:'var(--text-muted)',cursor:'pointer',padding:'2px 4px',
                            borderRadius:4,display:'inline-flex',alignItems:'center'}}>
                          <X size={12}/>
                        </button>
                      </div>
                    ) : tx.matched_documents?.length > 0 ? (
                      <div style={{display:'flex',flexDirection:'column',gap:4}}>
                        {tx.matched_documents.map((d, i) => {
                          // D26 (2026-09-27): nem-ADMIN-nak a bér-bizonylat helyén csonk jön
                          // (a párosítás TÉNYE igen, név/összeg/PDF nem) — nincs link, nincs bontás.
                          if (d.pay_hidden) return (
                            <div key={`pay-${i}`} style={{display:'flex',alignItems:'center',gap:6,
                              background:'rgba(16,185,129,0.10)',padding:'0.25rem 0.5rem',
                              borderRadius:'var(--radius)',fontSize:'0.72rem',color:'var(--text-muted)'}}>
                              <CheckCircle size={11} color="var(--accent)"/>
                              <span>{d.supplier_name}</span>
                            </div>
                          );
                          const docLink = d.pdf_onedrive_url ? documentPdfUrl(d.document_id) : previewUrl(d.document_id);
                          const label = d.supplier_name || d.customer_name || '–';
                          const confirmUnmatch = () => setConfirm({
                            title: 'Társítás eltávolítása',
                            message: `Biztosan eltávolítod a társítást? ${label}${d.invoice_number ? ' · ' + d.invoice_number : ''}`,
                            danger: true,
                            confirmLabel: 'Eltávolítás',
                            action: () => unmatchMut.mutate({txId:tx.id, docId:d.document_id}),
                          });
                          return (
                            <div key={d.document_id} style={{
                              display:'flex',alignItems:'center',gap:6,
                              background:'rgba(16,185,129,0.10)',padding:'0.25rem 0.5rem',
                              borderRadius:'var(--radius)',fontSize:'0.72rem'}}>
                              <CheckCircle size={11} color="var(--accent)"/>
                              <AuthedLink href={docLink}
                                title={d.pdf_onedrive_url ? 'PDF megnyitása' : 'Bizonylat előnézet'}
                                style={{color:'var(--primary)',textDecoration:'none',
                                  display:'inline-flex',alignItems:'center',gap:6,fontWeight:500}}>
                                <span>{label}</span>
                                {d.invoice_number && (
                                  <span style={{fontFamily:'var(--mono)',color:'var(--text-muted)',fontWeight:400}}>
                                    {d.invoice_number}
                                  </span>
                                )}
                              </AuthedLink>
                              <button onClick={confirmUnmatch}
                                title="Társítás eltávolítása"
                                style={{background:'rgba(239,68,68,0.10)',border:'1px solid rgba(239,68,68,0.25)',
                                  color:'#ef4444',cursor:'pointer',padding:'2px 4px',marginLeft:'auto',
                                  borderRadius:4,display:'inline-flex',alignItems:'center'}}>
                                <X size={14}/>
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div style={{display:'flex',alignItems:'center',gap:6}}>
                        <span style={{fontSize:'0.72rem',color:'var(--text-muted)'}}>nincs</span>
                        <button onClick={()=>setMatchTx(tx)}
                          title="Bizonylat keresése és csatolása"
                          style={{background:'rgba(59,130,246,0.10)',border:'1px solid rgba(59,130,246,0.3)',
                            color:'var(--primary)',cursor:'pointer',padding:'2px 6px',borderRadius:4,
                            fontSize:'0.65rem',fontWeight:600,display:'inline-flex',alignItems:'center',gap:3}}>
                          <Search size={11}/> Bizonylat
                        </button>
                        {isDebit && (
                          <button
                            onClick={()=>{ setFeeType('other'); setFeeModal({txId:tx.id}); }}
                            title="Banki díjnak jelöl"
                            style={{background:'rgba(245,158,11,0.08)',border:'1px solid rgba(245,158,11,0.3)',
                              color:'#f59e0b',cursor:'pointer',padding:'2px 6px',borderRadius:4,
                              fontSize:'0.65rem',fontWeight:600}}>
                            DÍJ?
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* ── Összegzés ─────────────────────────────────────────────────── */}
      {!isLoading && txs.length > 0 && (
        <StatementSummary stmt={stmt} txs={txs}/>
      )}

      <TxMatchModal open={!!matchTx} tx={matchTx} onClose={()=>setMatchTx(null)}
        onMatched={()=>{ qc.invalidateQueries({ queryKey: ['statement-txs', id] });
                         qc.invalidateQueries({ queryKey: ['statements'] }); }}/>

      <ConfirmDialog open={!!confirm} title={confirm?.title} message={confirm?.message}
        danger={confirm?.danger} confirmLabel={confirm?.confirmLabel}
        onCancel={()=>setConfirm(null)}
        onConfirm={()=>{ const a=confirm.action; setConfirm(null); a(); }}/>

      {/* Banki díj típus választó (a korábbi window.prompt kiváltása) */}
      <Modal open={!!feeModal} onClose={()=>setFeeModal(null)} title="Banki díjnak jelölés" width={380}>
        <Select label="Díj típusa" options={FEE_TYPE_OPTIONS} value={feeType}
          onChange={e=>setFeeType(e.target.value)}/>
        <div style={{display:'flex',gap:8,justifyContent:'flex-end',marginTop:'1rem'}}>
          <Btn variant="secondary" onClick={()=>setFeeModal(null)}>Mégse</Btn>
          <Btn onClick={()=>{
            feeMut.mutate({txId:feeModal.txId, isBankFee:true, feeType});
            setFeeModal(null);
          }}>
            Mentés
          </Btn>
        </div>
      </Modal>
    </div>
  );
}


// ── Összes tranzakció lista ────────────────────────────────────────────────
function AllTransactions({ tabBar }) {
  const [filters, setFilters] = useState({
    tx_type: '', currency: '', matched: '', q: '', account_id: '',
    date_from: '', date_to: '',
  });
  const toast = useToast();
  const { data: accounts = [], isError: accountsError, refetch: refetchAccounts } = useQuery({
    queryKey: ['statement-accounts'],
    queryFn: statementsApi.accounts,
  });
  const accountOptions = [{ value: '', label: 'Mind' },
    ...accounts.map(a => ({ value: a.id,
      label: `${a.bank_name || '?'}${a.currency ? ' · ' + a.currency : ''}` }))];
  const qc = useQueryClient();
  const [matchTx, setMatchTx] = useState(null);   // bizonylat-kereső modal tranzakciója
  // A2 (2026-09-06): Revolut tranzakciók a banki API-ból — strukturált (fee, MCC, bill_amount,
  // egyenleg), nem AI-olvasat; a PDF-ből már bent lévő tételt gazdagítja, nem duplikálja.
  // Sandbox környezetben a backend kihagyja (a játékpénz nem kerülhet a könyvelt tételek közé).
  const apiSyncMut = useMutation({
    mutationFn: () => statementsApi.apiSyncRevolut(90),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['transactions'] });
      qc.invalidateQueries({ queryKey: ['statements'] });
      if (r.skipped === 'sandbox') toast.info(`Revolut ${r.environment}: sandbox környezetben a szinkron kimarad.`);
      else toast.success(`Revolut API: ${r.inserted} új · ${r.enriched} gazdagított · ${r.matched} párosított tranzakció`);
    },
    onError: (err) => toast.error(err?.response?.data?.detail || 'A Revolut API-szinkron sikertelen.'),
  });
  const rematchMut = useMutation({
    mutationFn: statementsApi.rematchAll,
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['all-transactions'] });
      toast.success(`Újrapárosítva: +${data?.matched ?? 0} találat`);
    },
  });
  // A SZŰRŐK jele — lásd Documents.js azonos mintáját: a page/viewMode
  // SZÁNDÉKOSAN nincs benne, hogy a lenti useEffect csak valódi szűrő-
  // változásra állítsa 1-re a page-et, nézetváltásra vagy lapozásra ne.
  const filterParams = useMemo(() => {
    const p = Object.fromEntries(
      Object.entries(filters).filter(([k,v]) => v !== '' && v !== null)
        .map(([k,v]) => v === 'matched' ? [k, true]
                      : v === 'unmatched' ? [k, false]
                      : [k, v])
    );
    if (filters.matched === 'matched')   p.matched = true;
    if (filters.matched === 'unmatched') p.matched = false;
    delete p.matched_dropdown;
    return p;
  }, [filters]);

  const [page, setPage] = useState(1);
  const [viewMode, setViewMode] = useState('100');
  useEffect(() => { setPage(1); }, [filterParams]);
  const handleViewModeChange = (v) => { setViewMode(v); setPage(1); };

  const params = useMemo(() => ({ ...filterParams, page, page_size: viewMode }),
    [filterParams, page, viewMode]);

  const { data: txsPage, isLoading, isError: txsError, refetch: refetchTxs } = useQuery({
    queryKey: ['all-transactions', params],
    queryFn: () => statementsApi.allTransactions(params),
  });
  const txs = txsPage?.items ?? [];

  const tdS = { padding:'0.5rem 0.65rem', fontSize:'0.78rem', verticalAlign:'top',
                borderBottom:'1px solid var(--border-dim)' };
  const thS = { padding:'0.55rem 0.65rem', color:'var(--text-muted)', fontWeight:600,
                fontSize:'0.68rem', textTransform:'uppercase', letterSpacing:'0.06em',
                borderBottom:'1px solid var(--border)', whiteSpace:'nowrap' };

  return (
    <div>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',
        marginBottom:'1rem',flexWrap:'wrap',gap:8}}>
        <div>
          <h1 style={{fontSize:'1.25rem',fontWeight:700}}>Banki tranzakciók</h1>
          <p style={{fontSize:'0.8rem',color:'var(--text-muted)',marginTop:2}}>
            {txsPage ? `${txsPage.total_items} tranzakció` : '…'}
          </p>
        </div>
        <div style={{display:'flex',gap:8,alignItems:'center'}}>
          <Btn size="sm" variant="outline" icon={Banknote}
            loading={apiSyncMut.isPending}
            onClick={()=>apiSyncMut.mutate()}
            title="A Revolut tranzakciók behúzása a banki API-ból (utolsó 90 nap) — strukturált adat, nem AI-olvasat">
            Revolut API-szinkron
          </Btn>
          <Btn size="sm" variant="ghost" icon={RefreshCw}
            loading={rematchMut.isPending}
            onClick={()=>rematchMut.mutate()}
            title="Újrafuttatja a párosítást minden kivonatra (pl. késve érkezett számlák után)">
            Újrapárosítás
          </Btn>
        </div>
      </div>

      {tabBar}

      {accountsError && (
        <Alert type="danger" style={{display:'flex',alignItems:'center',
          justifyContent:'space-between',gap:12,flexWrap:'wrap'}}>
          <span>Betöltés sikertelen. (bankszámla-lista)</span>
          <Btn size="sm" variant="secondary" onClick={()=>refetchAccounts()}>Újra</Btn>
        </Alert>
      )}

      <Card style={{marginBottom:'0.75rem'}}>
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(140px,1fr))',gap:10}}>
          <Input label="Keresés (név/közlemény)" value={filters.q}
            onChange={e=>setFilters(f=>({...f,q:e.target.value}))}/>
          <Select label="Bank / számla" options={accountOptions} value={filters.account_id}
            onChange={e=>setFilters(f=>({...f,account_id:e.target.value}))}/>
          <Input label="Típus" type="text" placeholder="debit / credit / üres" value={filters.tx_type}
            onChange={e=>setFilters(f=>({...f,tx_type:e.target.value.toLowerCase()}))}/>
          <Input label="Match státusz" type="text" placeholder="matched / unmatched / üres" value={filters.matched}
            onChange={e=>setFilters(f=>({...f,matched:e.target.value.toLowerCase()}))}/>
          <Input label="Deviza" value={filters.currency}
            onChange={e=>setFilters(f=>({...f,currency:e.target.value.toUpperCase()}))}/>
          <Input label="Tól" type="date" value={filters.date_from}
            onChange={e=>setFilters(f=>({...f,date_from:e.target.value}))}/>
          <Input label="Ig" type="date" value={filters.date_to}
            onChange={e=>setFilters(f=>({...f,date_to:e.target.value}))}/>
        </div>
      </Card>

      {txsPage && (
        <div style={{display:'flex',justifyContent:'flex-end',marginBottom:8}}>
          <Pagination
            mode={txsPage.mode} page={txsPage.page} totalPages={txsPage.total_pages}
            pageLabel={txsPage.page_label}
            onPageChange={setPage} viewMode={viewMode} onViewModeChange={handleViewModeChange}
            showViewSelector/>
        </div>
      )}
      <Card noPad>
        <div style={{overflowX:'auto'}}>
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:'0.78rem'}}>
            <thead><tr>
              <th style={thS}>Dátum</th>
              <th style={thS}>Bank</th>
              <th style={thS}>Partner</th>
              <th style={thS}>Közlemény</th>
              <th style={{...thS,textAlign:'right'}}>Összeg</th>
              <th style={thS}>Match</th>
            </tr></thead>
            <tbody>
              {isLoading ? (
                <tr><td colSpan={6} style={{...tdS,textAlign:'center',padding:'1.5rem'}}><Spinner/></td></tr>
              ) : txsError ? (
                <tr><td colSpan={6} style={{...tdS,padding:'1rem'}}>
                  <Alert type="danger" style={{marginBottom:0,display:'flex',alignItems:'center',
                    justifyContent:'space-between',gap:12,flexWrap:'wrap'}}>
                    <span>Betöltés sikertelen.</span>
                    <Btn size="sm" variant="secondary" onClick={()=>refetchTxs()}>Újra</Btn>
                  </Alert>
                </td></tr>
              ) : txs.length === 0 ? (
                <tr><td colSpan={6} style={{...tdS,textAlign:'center',color:'var(--text-muted)',padding:'1.5rem'}}>
                  Nincs tranzakció a megadott szűrőkre.
                </td></tr>
              ) : txs.map(tx => {
                const isCredit = String(tx.tx_type).toLowerCase() === 'credit';
                return (
                  <tr key={tx.id}>
                    <td style={{...tdS,fontFamily:'var(--mono)',whiteSpace:'nowrap'}}>{fmtDate(tx.tx_date)}</td>
                    <td style={{...tdS,fontSize:'0.72rem',color:'var(--text-muted)'}}>
                      <span style={{display:'inline-flex',alignItems:'center',gap:6}}>
                        <BankDot name={tx.bank_name} size={8}/>
                        {tx.bank_name || '–'}
                      </span>
                    </td>
                    <td style={tdS}>
                      <div style={{fontWeight:500}}>{tx.counterparty_name || '–'}</div>
                      {tx.counterparty_iban && (
                        <div style={{fontSize:'0.66rem',color:'var(--text-muted)',fontFamily:'var(--mono)'}}>
                          {fmtIban(tx.counterparty_iban)}
                        </div>
                      )}
                    </td>
                    <td style={{...tdS,fontSize:'0.72rem',color:'var(--text-muted)',
                      maxWidth:320,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                      {tx.reference || '–'}
                    </td>
                    <td style={{...tdS,textAlign:'right',fontFamily:'var(--mono)',fontWeight:600,
                      color: isCredit ? 'var(--accent)' : 'inherit',whiteSpace:'nowrap'}}>
                      {isCredit ? '+' : '−'}{fmtAmt(tx.amount, tx.currency)}
                    </td>
                    <td style={tdS}>
                      {tx.is_fx && <FxBadge tx={tx}/>}
                      {tx.fx_kind === 'conversion' ? null : tx.is_bank_fee ? (
                        <FeeBadge feeType={tx.fee_type}/>
                      ) : tx.matched_documents?.length > 0 ? (
                        <div style={{display:'flex',flexDirection:'column',gap:3}}>
                          {tx.matched_documents.map((d, i) => {
                            // D26 / review L2 (2026-09-28): a bér-párosítás csonkja (id=null) — nincs link
                            if (d.pay_hidden) return (
                              <span key={`pay-${i}`} style={{display:'inline-flex',alignItems:'center',gap:4,
                                background:'rgba(16,185,129,0.10)',padding:'0.15rem 0.4rem',
                                borderRadius:'var(--radius)',fontSize:'0.7rem',color:'var(--text-muted)'}}>
                                <CheckCircle size={10} color="var(--accent)"/>{d.supplier_name}
                              </span>
                            );
                            const docLink = d.pdf_onedrive_url ? documentPdfUrl(d.id) : previewUrl(d.id);
                            return (
                              <AuthedLink key={d.id} href={docLink}
                                title={d.pdf_onedrive_url ? 'PDF megnyitása' : 'Bizonylat előnézet'}
                                style={{display:'inline-flex',alignItems:'center',gap:4,
                                  background:'rgba(16,185,129,0.10)',padding:'0.15rem 0.4rem',
                                  borderRadius:'var(--radius)',fontSize:'0.7rem',
                                  color:'var(--primary)',textDecoration:'none'}}>
                                <CheckCircle size={10} color="var(--accent)"/>
                                <span style={{fontWeight:500}}>{d.supplier_name || d.customer_name || '–'}</span>
                                {d.invoice_number && (
                                  <span style={{fontFamily:'var(--mono)',color:'var(--text-muted)',fontWeight:400}}>{d.invoice_number}</span>
                                )}
                              </AuthedLink>
                            );
                          })}
                        </div>
                      ) : (
                        <button onClick={()=>setMatchTx(tx)}
                          title="Bizonylat keresése és csatolása"
                          style={{background:'rgba(59,130,246,0.10)',border:'1px solid rgba(59,130,246,0.3)',
                            color:'var(--primary)',cursor:'pointer',padding:'2px 6px',borderRadius:4,
                            fontSize:'0.65rem',fontWeight:600,display:'inline-flex',alignItems:'center',gap:3}}>
                          <Search size={11}/> Bizonylat
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
      {txsPage && (
        <div style={{display:'flex',justifyContent:'flex-end',marginTop:8}}>
          <Pagination
            mode={txsPage.mode} page={txsPage.page} totalPages={txsPage.total_pages}
            pageLabel={txsPage.page_label}
            onPageChange={setPage} viewMode={viewMode} onViewModeChange={handleViewModeChange}/>
        </div>
      )}

      <TxMatchModal open={!!matchTx} tx={matchTx} onClose={()=>setMatchTx(null)}
        onMatched={()=>qc.invalidateQueries({ queryKey: ['all-transactions'] })}/>
    </div>
  );
}
